import React, { useState } from 'react';
import { FaEye, FaTimes, FaCopy, FaCheck } from 'react-icons/fa';
import { toast } from 'react-toastify';

/**
 * Reusable DescriptionViewer Component
 * Displays truncated description with "View Description" button that opens a modal
 * 
 * @param {string} description - The full description text
 * @param {number} wordLimit - Number of words to show initially (default: 5)
 * @param {string} className - Additional CSS classes
 * @param {string} textSize - Text size classes (default: 'text-[10px] sm:text-xs md:text-sm')
 * @param {string} buttonText - Custom button text (default: 'View Description')
 * @param {string} modalTitle - Custom modal title (default: 'Description')
 */
const DescriptionViewer = ({
  description = '',
  wordLimit = 5,
  className = '',
  textSize = 'text-[10px] sm:text-xs md:text-sm',
  buttonText = 'View Description',
  modalTitle = 'Description'
}) => {
  const [showModal, setShowModal] = useState(false);
  const [copied, setCopied] = useState(false);

  // Helper function to truncate text to specified word count
  const truncateText = (text, limit) => {
    if (!text) return '';
    const words = text.split(' ');
    if (words.length <= limit) return text;
    return words.slice(0, limit).join(' ') + '...';
  };

  const fullDescription = description || '';
  const truncatedDescription = truncateText(fullDescription, wordLimit);
  const shouldShowButton = fullDescription.split(' ').length > wordLimit;

  // Copy description function
  const copyDescription = async () => {
    if (!fullDescription) {
      toast.error('No description to copy');
      return;
    }
    try {
      await navigator.clipboard.writeText(fullDescription);
      setCopied(true);
      toast.success('Description copied to clipboard!');

      // Reset the copied state after animation
      setTimeout(() => {
        setCopied(false);
      }, 2000);
    } catch (error) {
      toast.error('Failed to copy description');
    }
  };

  return (
    <>
      <div className={`${className}`}>
        <div className={textSize}>
          <span>{truncatedDescription}</span>
          {shouldShowButton && (
            <button
              onClick={() => setShowModal(true)}
              className="ml-1 sm:ml-2 text-blue-600 hover:text-blue-800 font-medium text-[10px] sm:text-xs underline"
            >
              {buttonText}
            </button>
          )}
        </div>
      </div>

      {/* Modal for full description */}
      {showModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm transition-opacity duration-300"
          onClick={() => setShowModal(false)}
        >
          <div
            className="relative bg-white rounded-lg sm:rounded-xl shadow-2xl p-4 sm:p-6 md:p-8 max-w-2xl w-full mx-4 border border-gray-200 max-h-[80vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close Button */}
            <button
              className="absolute top-3 right-3 sm:top-4 sm:right-4 text-gray-400 hover:text-red-500 text-xl sm:text-2xl font-bold focus:outline-none focus:ring-2 focus:ring-red-300 transition z-10"
              onClick={() => setShowModal(false)}
              aria-label="Close"
            >
              <FaTimes />
            </button>

            {/* Modal Content */}
            <div className="flex flex-col">
              <div className="flex items-center justify-between mb-3 sm:mb-4 pr-8 sm:pr-10">
                <h2 className="text-lg sm:text-xl md:text-2xl font-bold text-gray-800 flex items-center gap-2">
                  <FaEye className="text-blue-500" /> {modalTitle}
                </h2>
                <button
                  onClick={copyDescription}
                  className={`flex items-center gap-1 px-1.5 sm:px-2 py-1 text-white text-[10px] sm:text-xs font-medium rounded transition-all duration-300 shadow-sm hover:shadow-md transform ${copied
                    ? 'bg-green-500 hover:bg-green-600 scale-110 animate-pulse'
                    : 'bg-blue-500 hover:bg-blue-600 hover:scale-105'
                    }`}
                  title={copied ? "Copied!" : "Copy description"}
                >
                  {copied ? (
                    <>
                      <FaCheck className="w-2.5 h-2.5 sm:w-3 sm:h-3 animate-bounce" />
                      <span className="hidden sm:inline text-[10px]">Copied!</span>
                    </>
                  ) : (
                    <>
                      <FaCopy className="w-2.5 h-2.5 sm:w-3 sm:h-3" />
                      <span className="hidden sm:inline text-[10px]">Copy</span>
                    </>
                  )}
                </button>
              </div>
              <div className="w-full bg-gray-50 rounded-lg sm:rounded-xl p-3 sm:p-4 md:p-6 border border-gray-200">
                <p className="text-sm sm:text-base md:text-lg text-gray-900 whitespace-pre-wrap leading-relaxed">
                  {fullDescription || 'No description available'}
                </p>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default DescriptionViewer;

